import { useCallback, useEffect, useMemo, useRef, useState, type RefObject, type UIEvent } from "react";

const SETTLE_IDLE_MS = 140;

const SWIPE_COMMIT_RATIO = 0.15;

function prefersReducedMotion(): boolean {
  return typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export interface SnapStripHandlers {
  onScroll: (event: UIEvent<HTMLDivElement>) => void;
  onTouchStart: () => void;
  onTouchEnd: () => void;
  onTouchCancel: () => void;
}

export interface SnapStrip {
  stripRef: RefObject<HTMLDivElement>;
  slideRef: (index: number) => (node: HTMLDivElement | null) => void;
  active: number;
  goTo: (index: number, behavior?: ScrollBehavior) => void;
  handlers: SnapStripHandlers;
}

export function useSnapStrip(count: number, resetKey?: string): SnapStrip {
  const [active, setActive] = useState(0);
  const stripRef = useRef<HTMLDivElement>(null);
  const slides = useRef<Array<HTMLDivElement | null>>([]);
  const slideCallbacks = useRef(new Map<number, (node: HTMLDivElement | null) => void>());
  const touching = useRef(false);
  const gesture = useRef<{ index: number; left: number } | null>(null);
  const timer = useRef<number | null>(null);

  const slideRef = useCallback((index: number) => {
    let callback = slideCallbacks.current.get(index);
    if (!callback) {
      callback = (node: HTMLDivElement | null) => {
        slides.current[index] = node;
      };
      slideCallbacks.current.set(index, callback);
    }
    return callback;
  }, []);

  const stepPx = useCallback(() => {
    const [first, second] = slides.current;
    if (first && second) return second.offsetLeft - first.offsetLeft;
    return stripRef.current?.clientWidth || 1;
  }, []);

  const clearTimer = useCallback(() => {
    if (timer.current !== null) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);

  const settle = useCallback(() => {
    clearTimer();
    const strip = stripRef.current;
    if (!strip || touching.current || count < 2) return;
    const step = stepPx();
    if (step <= 1) return;
    const position = strip.scrollLeft / step;
    const started = gesture.current;
    gesture.current = null;
    let target = Math.round(position);
    if (started) {
      if (position > started.index + SWIPE_COMMIT_RATIO) target = started.index + 1;
      else if (position < started.index - SWIPE_COMMIT_RATIO) target = started.index - 1;
      else target = started.index;
    }
    target = Math.max(0, Math.min(count - 1, target));
    const left = target * step;
    if (Math.abs(strip.scrollLeft - left) > 1) {
      strip.scrollTo({ left, behavior: prefersReducedMotion() ? "auto" : "smooth" });
    }
  }, [clearTimer, count, stepPx]);

  const scheduleSettle = useCallback(() => {
    clearTimer();
    timer.current = window.setTimeout(settle, SETTLE_IDLE_MS);
  }, [clearTimer, settle]);

  useEffect(() => {
    setActive(0);
    gesture.current = null;
    stripRef.current?.scrollTo({ left: 0 });
  }, [resetKey]);

  useEffect(() => {
    const strip = stripRef.current;
    if (!strip) return;
    const onScrollEnd = () => settle();
    strip.addEventListener("scrollend", onScrollEnd);
    return () => strip.removeEventListener("scrollend", onScrollEnd);
  }, [settle, count]);

  useEffect(() => clearTimer, [clearTimer]);

  const onScroll = useCallback(
    (event: UIEvent<HTMLDivElement>) => {
      const step = stepPx() || 1;
      const next = Math.max(0, Math.min(count - 1, Math.round(event.currentTarget.scrollLeft / step)));
      setActive((previous) => (previous === next ? previous : next));
      scheduleSettle();
    },
    [count, scheduleSettle, stepPx],
  );

  const onTouchStart = useCallback(() => {
    const strip = stripRef.current;
    if (!strip) return;
    touching.current = true;
    clearTimer();
    const step = stepPx() || 1;
    gesture.current = { index: Math.max(0, Math.min(count - 1, Math.round(strip.scrollLeft / step))), left: strip.scrollLeft };
  }, [clearTimer, count, stepPx]);

  const onTouchEnd = useCallback(() => {
    touching.current = false;
    const strip = stripRef.current;
    const started = gesture.current;
    if (strip && started && Math.abs(strip.scrollLeft - started.left) < 1) {
      gesture.current = null;
      return;
    }
    scheduleSettle();
  }, [scheduleSettle]);

  const goTo = useCallback(
    (index: number, behavior?: ScrollBehavior) => {
      const target = Math.max(0, Math.min(Math.max(count - 1, 0), index));
      setActive(target);
      gesture.current = null;
      clearTimer();
      stripRef.current?.scrollTo({ left: stepPx() * target, behavior: behavior ?? (prefersReducedMotion() ? "auto" : "smooth") });
    },
    [clearTimer, count, stepPx],
  );

  const handlers = useMemo<SnapStripHandlers>(
    () => ({ onScroll, onTouchStart, onTouchEnd, onTouchCancel: onTouchEnd }),
    [onScroll, onTouchEnd, onTouchStart],
  );

  return { stripRef, slideRef, active: Math.min(active, Math.max(count - 1, 0)), goTo, handlers };
}
